/**
 * NetDocuments Environment Utilities
 *
 * Iframe detection, origin validation, and trusted domain management
 * for the ND iframe integration. Mirrors Angular's EnvironmentService.
 */

// Base domain trusted for postMessage from mot-r subdomains. Deliberately explicit rather than
// taken from the page's hostname: on a shared host (*.azurewebsites.net) that would trust strangers.
const BASE_DOMAIN = import.meta.env.VITE_API_DOMAIN || 'mot-r.app';

// Trusted external domains allowed for postMessage communication
const TRUSTED_DOMAINS = ['https://vault.netvoyage.com'];

/**
 * Check whether the app is running inside an iframe.
 * Mirrors Angular's `isIframeEnvironment()`.
 */
export const isIframeEnvironment = (): boolean => {
  try {
    return window.location.ancestorOrigins.length > 0;
  } catch {
    // Fallback for browsers that don't support ancestorOrigins
    try {
      return window.self !== window.top;
    } catch {
      return true; // If we can't access window.top, we're in a cross-origin iframe
    }
  }
};

/**
 * Validate if an origin is a valid mot-r subdomain.
 * Mirrors Angular's `isValidMotrDomain()`.
 */
export const isValidMotrDomain = (origin: string): boolean => {
  try {
    const url = new URL(origin);
    return (
      url.hostname.endsWith('.' + BASE_DOMAIN) ||
      url.hostname === BASE_DOMAIN
    );
  } catch {
    return false;
  }
};

/**
 * Validate if an origin is allowed for postMessage communication.
 * Mirrors Angular's `isAllowedOrigin()`.
 */
export const isAllowedOrigin = (origin: string): boolean => {
  // Allow same origin
  if (origin === window.location.origin) {
    return true;
  }

  // Allow mot-r subdomains
  if (isValidMotrDomain(origin)) {
    return true;
  }

  // Allow trusted external domains (vault.netvoyage.com)
  if (TRUSTED_DOMAINS.includes(origin)) {
    return true;
  }

  return false;
};
